import { useState, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { sparkles } from 'ionicons/icons';
import type { CandidateProfile } from '@/types';

/**
 * Occupation-representative images shared by the signed-in ProfileCover and
 * the guest ProfileHero on /profile.
 */
export const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&h=500&fit=crop&q=75',
  'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&h=500&fit=crop&q=75',
];

/** "TwoWeeks" → "Two Weeks" without importing per-enum label maps. */
function prettifyEnum(value: string): string {
  return value.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/**
 * Signed-in cover banner for /profile — crossfading trade imagery behind a
 * personalised greeting. Mirrors the guest ProfileHero treatment so the page
 * keeps a photographic top on every visit.
 *
 * @see applications/bp-mobile-web-pwa/src/pages/dashboard/CandidateDashboard.css (.cd-cover)
 * @see applications/bp-mobile-web-pwa/src/pages/dashboard/CandidateDashboard.tsx (render site)
 */
export function ProfileCover({ profile, userName }: { profile: CandidateProfile; userName?: string }) {
  const firstName = (userName || '').trim().split(' ')[0] || 'there';
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroIdx((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const area = profile.desiredLocations?.[0]?.label;

  return (
    <div className="cd-cover">
      <div className="cd-cover-bg" aria-hidden="true">
        {HERO_IMAGES.map((src, i) => (
          <div
            key={src}
            className="cd-cover-slide"
            style={{
              backgroundImage: `url('${src}')`,
              opacity: i === heroIdx ? 1 : 0,
              transform: i === heroIdx ? 'scale(1.06)' : 'scale(1)',
            }}
          />
        ))}
        <div className="cd-cover-overlay" />
      </div>
      <div className="cd-guest-hero-glow cd-guest-hero-glow-1" aria-hidden="true" />
      <div className="cd-guest-hero-glow cd-guest-hero-glow-2" aria-hidden="true" />

      <div className="cd-cover-content">
        <div className="cd-cover-badge">
          <IonIcon icon={sparkles} style={{ fontSize: '0.625rem' }} />
          Your BluePages profile
        </div>
        <h1 className="cd-cover-heading">
          Welcome back, <span className="cd-cover-heading-accent">{firstName}</span>
        </h1>
        <div className="cd-cover-meta">
          <span className="cd-cover-chip">{prettifyEnum(profile.availability)}</span>
          <span className="cd-cover-chip">{prettifyEnum(profile.workType)}</span>
          {area && <span className="cd-cover-chip">{area}</span>}
        </div>
      </div>
    </div>
  );
}
